// Action Creators
// Constants
import { ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
import { LogicModule } from "@ms/planner-logicmodules/lib/local/modules/LogicModule";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Telemetry
import { TrackedInteractions } from "@ms/uno-telemetry/lib/local/performance/constants/InteractionRegistry";
// Utilities
import { checkIfNonNativeViewFromSubRouteType, extractPlanRoutingDetailsFromSubRoute, extractPrimaryViewIdFromRoute } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
/**
 * ActionCreator class for anything pertaining to performance data collection like EUPL and PLT.
 * Owns the full interaction tracking lifecycle:
 * - startInteraction: records start time in the store (both viewRender and manual strategies)
 * - viewRender: detects completed viewRender-strategy interactions, logs telemetry, resolves in the store
 * - completeInteraction: explicitly completes a manual-strategy interaction and logs telemetry
 */ export class PerformanceCollectionActionCreator {
    appStart(pageName, appStartTime) {}
    pageReady(dataFetchStart, dataFetchEnd, exportName, metaTag, extraData) {
        this.logViewRenderPerfMarkerForUnoNonNativeViews();
    }
    /**
     * Begin tracking a user interaction. Looks up the interaction in the
     * TrackedInteractions registry and records the start time in the store.
     * The target viewId is constructed from the registry entry's
     * viewName + the provided partitionKey for both strategies.
     *
     * If a pending entry already exists for the same target view, the first
     * one is kept (it is the real trigger) and the new interaction is logged
     * immediately as "abandoned".
     *
     * @param name Registry key (e.g. "OpenTaskEditor", "GridSort")
     * @param partitionKey Instance identifier (e.g. planId, taskId)
     * @param extraData Optional context logged with the telemetry event
     */ startInteraction(name, partitionKey, extraData) {
        const action = TrackedInteractions[name];
        if (!this.configProvider().settings.performanceSettings.enableRapTracking || !action) {
            return;
        }
        const startTime = performance.now();
        const targetViewId = {
            name: action.viewName,
            partitionKey
        };
        // If a pending entry already exists for this view, keep it — the first click is the real trigger.
        // Log the new (duplicate) interaction as abandoned so we have telemetry visibility.
        const perfStore = this.storeLoader.load(Store.PerformanceCollection);
        const existing = perfStore.getPendingInteractionByViewId(targetViewId);
        if (existing) {
            this.modules.load(LogicModule.PerformanceCollection).logInteraction(startTime, name, "abandoned", extraData);
            return;
        }
        this.modules.load(LogicModule.PerformanceCollection).startTrackedInteraction(name, startTime, targetViewId, action.completionStrategy, extraData);
    }
    /**
     * Called by useRenderTracker when a view renders. If the view reached
     * Critical state and a pending viewRender-strategy interaction targets it,
     * logs the duration and resolves the interaction before recording the view render state.
     */ viewRender(viewDetails, state, extraData) {
        // Check for pending interaction matching this view before dispatching
        if (this.configProvider().settings.performanceSettings.enableRapTracking && state >= ViewRenderState.Critical) {
            const perfStore = this.storeLoader.load(Store.PerformanceCollection);
            const pending = perfStore.getPendingInteractionByViewId(viewDetails);
            if (pending && pending.completionStrategy === "viewRender") {
                const module = this.modules.load(LogicModule.PerformanceCollection);
                // Log the interaction measurement, merging start-time and render-time metadata
                const mergedExtraData = {
                    ...pending.extraData,
                    ...extraData
                };
                module.logInteraction(pending.startTime, pending.name, "viewRender", mergedExtraData);
                // Mark it as completed in the store
                module.completeTrackedInteraction(viewDetails);
            }
        }
        this.modules.load(LogicModule.PerformanceCollection).setViewRenderState(viewDetails, state, extraData);
    }
    /**
     * Explicitly complete a pending manual-strategy interaction.
     * Reads from the store, logs RAP telemetry, and dispatches completion.
     */ completeInteraction(viewId) {
        const perfStore = this.storeLoader.load(Store.PerformanceCollection);
        const pending = perfStore.getPendingInteractionByViewId(viewId);
        if (!pending || pending.completionStrategy !== "manual") {
            return;
        }
        const module = this.modules.load(LogicModule.PerformanceCollection);
        module.logInteraction(pending.startTime, pending.name, "manual", pending.extraData);
        module.completeTrackedInteraction(viewId);
    }
    /** Logs view render perf marker for UNO non-native views on page ready. */ logViewRenderPerfMarkerForUnoNonNativeViews() {
        const userId = this.storeLoader.load(Store.AppContext).getAppContext().userId ?? "";
        const routeStore = this.storeLoader.load(Store.Route);
        const currentSubRouteType = routeStore.getCurrentSubRouteType();
        const currentSubRoute = routeStore.getCurrentSubRoute();
        // Don't log ViewRender marker on pageReady for native views
        if (!checkIfNonNativeViewFromSubRouteType(currentSubRouteType, this.configProvider)) {
            return;
        }
        this.viewRender(extractPrimaryViewIdFromRoute(currentSubRouteType, currentSubRoute, userId), ViewRenderState.Critical, {
            viewType: `${extractPlanRoutingDetailsFromSubRoute(currentSubRoute, currentSubRouteType)?.viewType}`
        });
    }
    /**
     * Initializes a new instance of the performance collector action creator
     * @param modules The logic module providers the SPA uses to get logic modules
     * @param storeLoader Store loader for reading store state
     * @param configProvider Configuration provider for feature flags
     */ constructor(modules, storeLoader, configProvider){
        this.modules = modules;
        this.storeLoader = storeLoader;
        this.configProvider = configProvider;
    }
}
